import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import Select from 'react-select';
import { Card, CardBody, CardHeader, Input } from "reactstrap";
import { BackendService, PunchEntry } from "../service/backend-service";
import { errorMessage } from "../util/error-utils";
import { StyledToast } from "../util/styled-toast";
import FilterProjectToggles, { FilterState } from "./filter-project-toggles";
import ReportsTable from "./reports-table";

interface Option {
  value: string,
  label: string
}

interface Filter {
  fromDate: string,
  toDate: string,
  favorite: FilterState,
  active: FilterState,
  projects: Array<Option>,
  fromSprint: number,
  toSprint: number,
  workDescription: string
}

function Reports() {
  const [loading, setLoading] = useState(false);
  const [filteredByDatePunches, setFilteredByDatePunches] = useState<Array<PunchEntry>>([]);
  const [filteredPunches, setFilteredPunches] = useState<Array<PunchEntry>>([]);
  const [projectOptions, setProjectOptions] = useState<Array<Option>>([]);
  const [filter, setFilter] = useState<Filter>({
    fromDate: moment().utc().startOf('year').format('YYYY-MM-DD'),
    toDate: moment().utc().add(1, 'day').format('YYYY-MM-DD'),
    favorite: FilterState.DISABLED,
    active: FilterState.DISABLED,
    projects: [],
    fromSprint: 1,
    toSprint: 10000,
    workDescription: ''
  });

  let auth0 = useAuth0();
  const service = useMemo(() => {
    return new BackendService(auth0);
  }, [auth0]);

  useEffect(() => {
    setLoading(true);
    service.getProjects()
      .then(projects => setProjectOptions(projects.map(project => {
        return {
          value: '' + project.id,
          label: project.name
        }
      })))
      .catch((ex: any) => StyledToast.error(errorMessage(ex)))
      .finally(() => setLoading(false));
  }, [service]);

  useEffect(() => {
    setLoading(true);
    service.getPunchesByDate(filter.fromDate, filter.toDate)
      .then(filtered => setFilteredByDatePunches(filtered))
      .catch((ex: any) => StyledToast.error(errorMessage(ex)))
      .finally(() => setLoading(false));
  }, [filter.fromDate, filter.toDate, service]);

  useEffect(() => {
    function applyFilters(list: Array<PunchEntry>) {
      let filteredList = list
        .filter(punch => punch.project.active === (filter.active === FilterState.ON) || filter.active === FilterState.DISABLED) // active
        .filter(punch => punch.project.favorite === (filter.favorite === FilterState.ON) || filter.favorite === FilterState.DISABLED) // favorite
        .filter(punch => punch.sprintId >= filter.fromSprint && punch.sprintId <= filter.toSprint) // sprint
        .filter(punch => filter.projects.length === 0 || filter.projects.map(opt => opt.value).find(val => val === '' + punch.project.id) !== undefined) // projects
        .filter(punch => punch.workDescription.includes(filter.workDescription)); // workDescription
      setFilteredPunches(filteredList);
    }

    applyFilters(filteredByDatePunches);
  }, [filter.active, filter.favorite, filter.fromSprint, filter.toSprint, filter.projects, filter.workDescription, filteredByDatePunches]);

  function onItemChanged(changedItem :PunchEntry) {
    setFilteredByDatePunches(filteredByDatePunches.map(it => it.id === changedItem.id ? changedItem : it));
  }

  function updateFilter(change: Partial<Filter>) {
    setFilter({ ...filter, ...change });
  }

  return (
    <>
      <Card className='mt-3'>
        <CardHeader className="alert-primary fw-bold">
          <i className='fas fa-fw fa-filter me-2' />
          Filter
        </CardHeader>
        <CardBody className='row row-cols-sm-1 row-cols-md-2 row-cols-lg-3 row-cols-xxl-4'>
          <div className="col mb-3">
            <label className="form-label"><i className='fas fa-fw fa-calendar-week me-2' />Date Range</label>
            <div className="input-group">
              <Input type="date" value={filter.fromDate} onChange={e => updateFilter({ fromDate: e.target.value })} />
              <Input type="date" value={filter.toDate} onChange={e => updateFilter({ toDate: e.target.value })} />
            </div>
          </div>
          <div className="col mb-3">
            <label className="form-label"><i className='fas fa-fw fa-ellipsis-vertical me-2' />Project Attributes</label>
            <FilterProjectToggles
              favorite={{
                value: filter.favorite,
                change: newValue => updateFilter({ favorite: newValue })
              }}
              active={{
                value: filter.active,
                change: newValue => updateFilter({ active: newValue })
              }}
            />
          </div>
          <div className="col mb-3">
            <label className="form-label"><i className='fas fa-fw fa-business-time me-2' />Projects</label>
            <Select value={filter.projects} isMulti={true} options={projectOptions} onChange={selecteds => updateFilter({ projects: [...selecteds] })} />
          </div>
          <div className="col mb-3">
            <label className="form-label"><i className='fas fa-fw fa-person-running me-2' />Sprint Range</label>
            <div className="input-group">
              <Input type="number" value={filter.fromSprint} onChange={e => updateFilter({ fromSprint: Number.parseInt(e.target.value) })} />
              <Input type="number" value={filter.toSprint} onChange={e => updateFilter({ toSprint: Number.parseInt(e.target.value) })} />
            </div>
          </div>
          <div className="col mb-3">
            <label className="form-label"><i className='far fa-fw fa-comment-dots me-2' />Work Description</label>
            <Input type="text" value={filter.workDescription} onChange={(e) => updateFilter({ workDescription: e.target.value })} />
          </div>
        </CardBody>
      </Card>
      <Card className='mt-3'>
        <CardHeader className="alert-success fw-bold">
          <span className="position-relative">
            <i className='fas fa-fw fa-chart-bar me-2' />
            Results
            <span className="report-results-badge position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary ms-3 mt-1">
              {filteredPunches.length}
            </span>
          </span>
        </CardHeader>
        {
          loading ?
            <CardBody className="pb-0">
              <div className="loading-spinner text-center w-100">
                <div className="spinner-border text-warning" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </CardBody>
            :
            <CardBody className='table-responsive pt-0 pb-0'>
              <ReportsTable punches={filteredPunches} onItemChange={onItemChanged}/>
            </CardBody>
        }
      </Card>
    </>
  );
}

export default Reports;
